import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "reduxStore/actions/productActions";
import { checkRoles } from "utils/permissions";
import { EditOutlined } from "@mui/icons-material";
import { TextField } from "@mui/material";

export const useProductEmissionsGrid = ({ handleEditOpen }) => {
  const { palette } = useTheme();
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const user = useSelector((state) => state.user || {});
  const organisationId = user.organisation?._id;
  const { isSystemAdmin } = checkRoles(user);

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const dispatch = useDispatch();
  const products = useSelector((state) => state.products.data || []);
  const totalProducts = useSelector((state) => state.products.total || 0);

  // selection state
  const [selectedProducts, setSelectedProducts] = useState([]);

  // robust fallback helper
  const getPreferredEmission = (ems) => {
    if (!ems || typeof ems !== "object") return 0;
    const {
      cradle_to_processing_gate_plus_transport: c2pgt,
      cradle_to_farm_gate: c2fg,
      processing = 0,
      packaging = 0,
      transport = 0,
    } = ems;

    if (c2pgt != null) return c2pgt;
    if (c2fg != null) return c2fg;
    return processing + packaging + transport;
  };

  const formatNumber = (num, decimals = 0) => {
    return num
      ?.toFixed?.(decimals)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // build top-level rows
  const rows = useMemo(
    () =>
      (products || []).map((prod) => {
        const ingredients = Array.isArray(prod.ingredients)
          ? prod.ingredients
          : [];

        const totalCo2PerKg = ingredients.reduce((sum, ing) => {
          const ems = ing?.emissionRecordId?.emissions; // <- guard
          const base = getPreferredEmission(ems);
          const pct = Number(ing?.percent) || 0;
          return sum + (base * pct) / 100;
        }, 0);

        const weight = Number(prod?.weight) || 0; // grams
        const co2PerProduct = (totalCo2PerKg * weight) / 1000; // kg per product

        const productQualityScore = ingredients.reduce((sum, ing) => {
          const dq = Number(ing?.emissionRecordId?.data_quality_score) || 0;
          const pct = Number(ing?.percent) || 0;
          return sum + (dq * pct) / 100;
        }, 0);

        return {
          id: prod._id,
          name: prod.name,
          organisationName: prod.organisation?.name ?? "",
          organisation: prod.organisation,
          weight,
          totalCo2PerKg,
          co2PerProduct,
          productQualityScore,
          ingredients,
        };
      }),
    [products]
  );

  // selection handlers
  const handleSelectionChange = (selectionModel) => {
    const selected = rows.filter((r) => selectionModel.includes(r.id));

    setSelectedProducts((prev) =>
      selected.map((p) => {
        const existing = prev.find((sp) => sp.id === p.id);
        return {
          ...p,
          quantity: existing ? existing.quantity : 1,
        };
      })
    );
  };

  const handleQuantityChange = (id, value) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              quantity:
                value === "" ? "" : Math.max(parseInt(value, 10) || 0, 0),
            }
          : p
      )
    );
  };

  const totalCarbonOutput = useMemo(() => {
    const total = selectedProducts.reduce((sum, p) => {
      const qty = p.quantity === "" ? 1 : Number(p.quantity) || 1;
      return sum + (Number(p.co2PerProduct) || 0) * qty;
    }, 0);
    return Number(total.toFixed(1)); // keep as a number
  }, [selectedProducts]);

  // columns for the comparison grid
  const selectedColumns = [
    { field: "name", headerName: "Product Name", flex: 1 },
    {
      field: "co2PerProduct",
      headerName: "CO₂e per unit",
      flex: 0.5,
      renderCell: (params) => `${params.value.toFixed(3)} kg`,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 0.5,
      renderCell: (params) => {
        const handleChange = (e) => {
          const value = e.target.value.replace(/,/g, ""); // Remove commas before parsing
          handleQuantityChange(params.row.id, value);
        };

        const handleBlur = () => {
          if (params.row.quantity === "" || params.row.quantity <= 0) {
            handleQuantityChange(params.row.id, 1);
          }
        };

        const formattedValue =
          params.row.quantity !== "" && !isNaN(params.row.quantity)
            ? formatNumber(params.row.quantity)
            : "";

        return (
          <TextField
            type="text"
            variant="outlined"
            size="small"
            value={formattedValue}
            onChange={handleChange}
            onBlur={handleBlur}
            margin="dense"
            inputProps={{ min: 1 }}
          />
        );
      },
    },

    {
      field: "total",
      headerName: "Total CO₂e (kg)",
      flex: 0.5,
      renderCell: (params) =>
        `${formatNumber(params.row.co2PerProduct * params.row.quantity, 1)} kg`,
    },
    {
      field: "totalTonnes",
      headerName: "Total CO₂e (Tonnes)",
      flex: 0.5,
      renderCell: (params) =>
        `${formatNumber(
          (params.row.co2PerProduct * params.row.quantity) / 1000,
          1
        )} tonnes`,
    },
  ];

  // Top‐level columns, now showing the product’s quality score
  const columns = [
    { field: "name", headerName: "Product", flex: 2 },
    {
      field: "organisationName",
      headerName: "Organisation",
      flex: 2,
      sortable: false,
    },
    {
      field: "weight",
      headerName: "Weight (g)",
      type: "number",
      flex: 1,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const v = params.row.weight;
        return v != null ? `${formatNumber(v)} g` : "";
      },
    },
    {
      field: "totalCo2PerKg",
      headerName: "Total CO₂e/kg",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const v = params.row.totalCo2PerKg;
        let bg = "";
        if (v < 1.75) bg = "#00BCD4";
        else if (v < 3) bg = "#4CAF50";
        else if (v < 4.25) bg = "#FF9800";
        else if (v < 5.5) bg = "#F44336";
        else bg = "#B71C1C";
        return (
          <div
            style={{
              backgroundColor: bg,
              color: "white",
              borderRadius: 4,
              textAlign: "center",
            }}
          >
            {v != null ? formatNumber(v, 2) : ""}
          </div>
        );
      },
    },
    {
      field: "co2PerProduct",
      headerName: "CO₂ per product",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const v = params.row.co2PerProduct;
        return v != null ? formatNumber(params.row.co2PerProduct, 2) : "";
      },
    },
    isSystemAdmin && {
      field: "productQualityScore",
      headerName: "Data Quality Score",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const v = params.row.productQualityScore;
        return v != null ? formatNumber(v, 2) : "";
      },
    },

    {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <button
          onClick={() => handleEditOpen(params.row)}
          style={{ background: "transparent", border: "none" }}
        >
          <EditOutlined sx={{ color: primaryDark }} />
        </button>
      ),
    },
  ].filter(Boolean); // Remove any undefined values (e.g., if not admin)

  // Detail‐panel columns: add a dataQualityScore column here as well
  const ingredientColumns = [
    { field: "ingredientName", headerName: "Ingredient", flex: 2 },
    {
      field: "percent",
      headerName: "% of product",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        params.row.percent != null ? `${params.row.percent}%` : "",
    },
    {
      field: "totalCo2",
      headerName: "Total CO₂e/kg",
      flex: 1,

      renderCell: (params) => {
        const v = params.row.totalCo2;
        let bg = "";
        if (v < 1.75) bg = "#00BCD4";
        else if (v < 3) bg = "#4CAF50";
        else if (v < 4.25) bg = "#FF9800";
        else if (v < 5.5) bg = "#F44336";
        else bg = "#B71C1C";
        return (
          <div
            style={{
              backgroundColor: bg,
              color: "white",
              borderRadius: 4,
              textAlign: "center",
            }}
          >
            {v != null ? v.toFixed(2) : ""}
          </div>
        );
      },
    },
    {
      field: "co2PerKg",
      headerName: "CO₂e/kg/product ",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        params.row.co2PerKg != null ? params.row.co2PerKg.toFixed(2) : "",
    },
    isSystemAdmin && {
      field: "dataQualityScore",
      headerName: "Data Quality Score",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        params.row.dataQualityScore != null
          ? params.row.dataQualityScore.toFixed(2)
          : "",
    },
  ].filter(Boolean); // Remove any undefined values (e.g., if not admin)

  useEffect(() => {
    dispatch(
      fetchProducts({
        page,
        limit,
        sort,
        search,
        organisation: isSystemAdmin ? undefined : organisationId,
      })
    );
  }, [page, limit, sort, search, dispatch, organisationId, isSystemAdmin]);

  useEffect(() => {
    if (products.length) {
      setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
    }
  }, [products]);

  // 1) When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    const h = setTimeout(() => {
      setSearch(searchInput);
      setPage(0); // back to first page
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput]);

  // 2) Reset to page 1 when sort changes
  useEffect(() => {
    setPage(0);
  }, [sort]);

  return {
    backgroundAlt,
    primaryLight,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    sort,
    setSort,
    searchInput,
    setSearchInput,
    rows,
    columns,
    totalProducts,
    ingredientColumns,
    getPreferredEmission,
    selectedProducts,
    handleSelectionChange,
    selectedColumns,
    totalCarbonOutput,
    formatNumber,
  };
};
